/* eslint-disable max-len */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../../modules/auth/useAuth";
import { useTranslation } from "react-i18next";
import { Spinner } from "@xfrontend/xui-react-plus";
import {
  getLicense,
  getDashboardData,
} from "#/modules/dashboard/redux/actionCreator";
import { getPolicies } from "#/modules/policies/slice";
import { fetchServiceTokens } from "#/infra/redux/actionCreator";
import PanelProvider from "#/modules/common/PanelProvider";
import BotServiceSection from "./components/BotServiceSection";
import ThreatTraceSection from "./components/ThreatTraceSection";
import DashboardHeader from "./components/DashboardHeader";
import { RootState } from "#/types/RootState";
import { DashboardItem } from "#/apis/infra/getDashboard";
import "./index.scss";

const Dashboard = (): JSX.Element => {
  const auth = useAuth();
  const dispatch = useDispatch();
  const { t } = useTranslation("dashboard");
  const dashboardLoading: boolean = useSelector(
    (state: RootState) => state.dashboard.dashboardData.loading,
  );
  const dashboardDataRaw = useSelector(
    (state: RootState) => state.dashboard.dashboardData.results,
  );
  const dashboardData: DashboardItem[] = Array.isArray(dashboardDataRaw) ? dashboardDataRaw : [];
  const [period] = useState(7);

  const activeKeyMap = dashboardData.map((item) => ({ key: item.division || t("uncategorized"), show: true, expand: true }));

  const defaultActiveKey = activeKeyMap
    .filter((item) => item.show && item.expand)
    .map((item) => item.key);

  const dashboardAccordionActiveKey = localStorage.getItem("dashboardAccordionActiveKey")
    ? JSON.parse(localStorage.getItem("dashboardAccordionActiveKey") || JSON.stringify(defaultActiveKey))
    : defaultActiveKey;

  const [activeKey, setActiveKey] = useState(dashboardAccordionActiveKey);

  const isAllExpanded = activeKey?.every((key) => {
    const matched = activeKeyMap.find((item) => item.key === key);

    return matched ? !matched.expand : true;
  });

  useEffect(() => {
    if (auth.isAuthenticated) {
      dispatch(getLicense(auth.user?.access_token ?? ""));
      dispatch(fetchServiceTokens(auth.user?.access_token ?? ""));
      dispatch(
        getDashboardData({
          accessToken: auth.user?.access_token ?? "",
          period,
        }),
      );
      dispatch(
        getPolicies({
          accessToken: auth.user?.access_token ?? "",
          period,
        }),
      );
    }
  }, [auth.isAuthenticated, dispatch, period]);

  return (
    <PanelProvider>
      <div style={{ padding: "12px" }}>
        <DashboardHeader
          title={t("title")}
          actionText={isAllExpanded ? t("actions.expandAll") : t("actions.collapseAll")}
          onActionClick={() => {
            setActiveKey(isAllExpanded ? defaultActiveKey : []);
            localStorage.setItem(
              "dashboardAccordionActiveKey",
              JSON.stringify(isAllExpanded ? defaultActiveKey : []),
            );
          }}
        />
        {dashboardLoading ? (
          <div style={{
            display: "flex", justifyContent: "center", alignItems: "center", padding: "80px 0",
          }}
          >
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <BotServiceSection data={dashboardData} activeKey={activeKey} setActiveKey={setActiveKey} />
        )}
        <ThreatTraceSection />
      </div>
    </PanelProvider>
  );
};

export default Dashboard;
